import { useEffect, useRef, useState } from 'react';

const REDUCED_MOTION_MEDIA = '(prefers-reduced-motion: reduce)';
const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);

/**
 * useCountUp - animate a number from `from` to `to` over `duration` ms.
 * Respects prefers-reduced-motion by snapping to the final value.
 */
export function useCountUp(to, options = {}) {
  const { duration = 900, from = 0 } = options;
  const [value, setValue] = useState(from);
  const rafRef = useRef(null);

  useEffect(() => {
    if (typeof to !== 'number' || Number.isNaN(to)) {
      setValue(0);
      return undefined;
    }

    const reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(REDUCED_MOTION_MEDIA).matches;

    if (reducedMotion) {
      setValue(to);
      return undefined;
    }

    const start = performance.now();
    const startVal = from;

    const tick = (now) => {
      const elapsed = now - start;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeOutCubic(progress);
      setValue(startVal + (to - startVal) * eased);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [to, duration, from]);

  return value;
}
